'use client';

/**
 * Currency Context
 *
 * Provides currency preference and conversion functionality to all components.
 * Implements Option 4: Lazy rate fetch with client-side caching.
 *
 * Features:
 * - User currency preference persistence (localStorage)
 * - Lazy loading of exchange rates (only when needed)
 * - Stale-while-revalidate caching strategy
 * - Client-side currency conversion
 *
 * Usage:
 * ```tsx
 * // In a Client Component
 * const { selectedCurrency, setSelectedCurrency, convert } = useCurrency();
 *
 * // Convert a price
 * const priceInSelectedCurrency = convert(18000000, 'MUR');
 * ```
 */

import type { Currency } from '@/lib/config/app.config';
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '@/lib/config/app.config';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';

// =============================================================================
// CONSTANTS
// =============================================================================

/** localStorage key for cached exchange rates */
const RATES_CACHE_KEY = 'rr_exchange_rates';

/** localStorage key for user's currency preference */
const CURRENCY_PREF_KEY = 'rr_selected_currency';

/** Cache TTL: 2 hours (rates change daily, sync hourly) */
const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

/** Stale threshold: 1 hour (background refresh after this) */
const STALE_THRESHOLD_MS = 1 * 60 * 60 * 1000;

// =============================================================================
// TYPES
// =============================================================================

interface CachedRates {
  rates: Record<string, number>;
  baseCurrency: string;
  lastUpdated: string | null;
  cachedAt: number;
}

/**
 * Currency context value type
 */
export interface CurrencyContextValue {
  /** Currently selected currency for display */
  selectedCurrency: Currency;

  /** Set the selected currency */
  setSelectedCurrency: (currency: Currency) => void;

  /** Exchange rates (null if not yet loaded) */
  rates: Record<string, number> | null;

  /** Whether rates are currently loading */
  isLoading: boolean;

  /** Whether rates are stale and should be refreshed */
  isStale: boolean;

  /** Error message if rate fetch failed */
  error: string | null;

  /** When rates were last updated from the provider */
  ratesLastUpdated: string | null;

  /**
   * Convert an amount from one currency to the selected currency
   * Returns the original amount if conversion is not possible
   */
  convert: (amount: number, fromCurrency: string) => number;

  /**
   * Convert an amount between any two currencies
   * Returns null if conversion is not possible
   */
  convertBetween: (
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ) => number | null;

  /** Manually trigger a rate refresh */
  refreshRates: () => Promise<void>;

  /** List of supported currencies */
  supportedCurrencies: readonly Currency[];
}

// =============================================================================
// CONTEXT
// =============================================================================

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Get cached rates from localStorage
 */
function getCachedRates(): CachedRates | null {
  if (!isBrowser()) return null;

  try {
    const cached = localStorage.getItem(RATES_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedRates;

    // Check if cache is expired
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(RATES_CACHE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Save rates to localStorage cache
 */
function setCachedRates(
  rates: Record<string, number>,
  baseCurrency: string,
  lastUpdated: string | null
): void {
  if (!isBrowser()) return;

  try {
    const cache: CachedRates = {
      rates,
      baseCurrency,
      lastUpdated,
      cachedAt: Date.now(),
    };
    localStorage.setItem(RATES_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore localStorage errors (quota exceeded, etc.)
  }
}

/**
 * Get saved currency preference from localStorage
 */
function getSavedCurrencyPreference(): Currency | null {
  if (!isBrowser()) return null;

  try {
    const saved = localStorage.getItem(CURRENCY_PREF_KEY);
    if (saved && SUPPORTED_CURRENCIES.includes(saved as Currency)) {
      return saved as Currency;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save currency preference to localStorage
 */
function saveCurrencyPreference(currency: Currency): void {
  if (!isBrowser()) return;

  try {
    localStorage.setItem(CURRENCY_PREF_KEY, currency);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Check if cached rates are stale (should trigger background refresh)
 */
function areCachedRatesStale(cachedAt: number): boolean {
  return Date.now() - cachedAt > STALE_THRESHOLD_MS;
}

// =============================================================================
// PROVIDER
// =============================================================================

export interface CurrencyProviderProps {
  children: ReactNode;
  /** Initial currency (e.g., from tenant config) */
  defaultCurrency?: Currency;
}

/**
 * Currency Provider Component
 *
 * Provides currency context to the application.
 * Should be placed near the root of the app, after TenantProvider.
 */
export function CurrencyProvider({
  children,
  defaultCurrency = DEFAULT_CURRENCY,
}: CurrencyProviderProps) {
  // State
  const [selectedCurrency, setSelectedCurrencyState] =
    useState<Currency>(defaultCurrency);
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ratesLastUpdated, setRatesLastUpdated] = useState<string | null>(null);
  const [hasInitialised, setHasInitialised] = useState(false);

  // Load saved preference and cached rates on mount
  useEffect(() => {
    if (hasInitialised) return;

    // Load saved currency preference
    const savedPreference = getSavedCurrencyPreference();
    
    if (savedPreference) {
      setSelectedCurrencyState(savedPreference);
    }

    // Load cached rates
    const cached = getCachedRates();
    
    if (cached) {
      setRates(cached.rates);
      setRatesLastUpdated(cached.lastUpdated);
      setIsStale(areCachedRatesStale(cached.cachedAt));
    }

    setHasInitialised(true);
  }, [hasInitialised, defaultCurrency]);
  
  // Pre-fetch rates on mount to ensure conversions are always possible
  // Uses a ref to track fetch attempts and avoid duplicate/infinite fetches
  const preFetchAttemptRef = useRef(0);
  const MAX_PRE_FETCH_RETRIES = 3;
  const PRE_FETCH_RETRY_DELAYS = [2000, 5000, 10000]; // Exponential backoff
  
  useEffect(() => {
    if (!hasInitialised || isLoading || rates) return;
    if (preFetchAttemptRef.current >= MAX_PRE_FETCH_RETRIES) return;
    
    const attemptFetch = async () => {
      const attempt = preFetchAttemptRef.current;
      preFetchAttemptRef.current += 1;
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch('/api/exchange-rates');
        if (response.ok) {
          const data = await response.json();
          if (data.rates && typeof data.rates === 'object' && Object.keys(data.rates).length > 0) {
            setRates(data.rates);
            setRatesLastUpdated(data.lastUpdated);
            setIsStale(false);
            setCachedRates(data.rates, data.baseCurrency || 'USD', data.lastUpdated);
          } else {
            // Empty rates is expected when Route Royal hasn't populated exchange rates yet
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CurrencyContext] Exchange rates not yet available, prices will show in original currency');
            }
            scheduleRetry(attempt);
          }
        } else {
          console.warn('[CurrencyContext] Pre-fetch failed:', response.status);
          scheduleRetry(attempt);
        }
      } catch (err) {
        console.warn('[CurrencyContext] Pre-fetch error:', err);
        scheduleRetry(attempt);
      } finally {
        setIsLoading(false);
      }
    };
    
    const scheduleRetry = (attempt: number) => {
      if (attempt < MAX_PRE_FETCH_RETRIES - 1) {
        const delay = PRE_FETCH_RETRY_DELAYS[attempt] ?? 10000;
        setTimeout(() => {
          // Trigger re-render to re-run this effect
          setError(`Retrying exchange rates (attempt ${attempt + 2}/${MAX_PRE_FETCH_RETRIES})...`);
        }, delay);
      } else {
        setError('Unable to load exchange rates. Prices shown in original currency.');
      }
    };
    
    void attemptFetch();
  }, [hasInitialised, rates, error]);

  // Handle currency selection - inline fetch to avoid dependency on fetchRates
  const setSelectedCurrency = useCallback(
    (currency: Currency) => {
      setSelectedCurrencyState(currency);
      saveCurrencyPreference(currency);

      // Fetch rates if we don't have them and user is switching currencies
      if (!rates && !isLoading) {
        setIsLoading(true);
        setError(null);
        fetch('/api/exchange-rates')
          .then(res => {
            if (!res.ok) {
              return Promise.reject(new Error(`HTTP ${res.status}`));
            }
            return res.json();
          })
          .then(data => {
            if (data.rates && typeof data.rates === 'object' && Object.keys(data.rates).length > 0) {
              setRates(data.rates);
              setRatesLastUpdated(data.lastUpdated);
              setIsStale(false);
              setCachedRates(data.rates, data.baseCurrency || 'USD', data.lastUpdated);
            } else {
              setError('Exchange rates unavailable. Prices shown in original currency.');
            }
          })
          .catch(err => {
            console.error('[CurrencyContext] Fetch error on currency change:', err);
            setError('Unable to load exchange rates. Prices shown in original currency.');
          })
          .finally(() => setIsLoading(false));
      }
    },
    [rates, isLoading]
  );

  // Background refresh if rates are stale - inline fetch
  // This should only run once when rates are stale, then set isStale to false
  useEffect(() => {
    if (isStale && !isLoading && hasInitialised) {
      setIsLoading(true);
      // Set isStale to false immediately to prevent re-triggering
      setIsStale(false);
      fetch('/api/exchange-rates')
        .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
        .then(data => {
          if (data.rates) {
            setRates(data.rates);
            setRatesLastUpdated(data.lastUpdated);
            // After a successful fetch, rates are fresh - don't set isStale from API response
            // The API's isStale flag indicates server-side staleness, not client-side
            setCachedRates(data.rates, data.baseCurrency || 'USD', data.lastUpdated);
          }
        })
        .catch(() => {
          // Background refresh failed silently
        })
        .finally(() => setIsLoading(false));
    }
  }, [isStale, isLoading, hasInitialised]);

  // Convert between currencies using triangulation via USD
  const convertBetween = useCallback(
    (
      amount: number,
      fromCurrency: string,
      toCurrency: string
    ): number | null => {
      // Same currency - no conversion needed
      if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
        return amount;
      }

      // No rates available
      if (!rates) {
        return null;
      }

      const from = fromCurrency.toUpperCase();
      const to = toCurrency.toUpperCase();

      // Get rates (all are relative to USD)
      const fromRate = from === 'USD' ? 1 : rates[from];
      const toRate = to === 'USD' ? 1 : rates[to];

      if (fromRate === undefined || toRate === undefined) {
        console.warn('[CurrencyProvider] Missing rate for conversion:', {
          from,
          fromRate,
          to,
          toRate,
        });
        return null;
      }

      // Convert: amount in fromCurrency → USD → toCurrency
      // Formula: targetAmount = sourceAmount × (targetRate / sourceRate)
      const convertedAmount = amount * (toRate / fromRate);

      // Round to 2 decimal places
      return Math.round(convertedAmount * 100) / 100;
    },
    [rates]
  );

  // Convert to selected currency
  const convert = useCallback(
    (amount: number, fromCurrency: string): number => {
      const result = convertBetween(amount, fromCurrency, selectedCurrency);
      // Return original amount if conversion not possible
      return result ?? amount;
    },
    [convertBetween, selectedCurrency]
  );

  // Manual refresh function - inline to avoid dependency issues
  const refreshRates = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/exchange-rates');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.rates) {
        setRates(data.rates);
        setRatesLastUpdated(data.lastUpdated);
        // After a successful fetch, rates are fresh
        setIsStale(false);
        setCachedRates(data.rates, data.baseCurrency || 'USD', data.lastUpdated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Memoised context value
  const value = useMemo<CurrencyContextValue>(
    () => ({
      selectedCurrency,
      setSelectedCurrency,
      rates,
      isLoading,
      isStale,
      error,
      ratesLastUpdated,
      convert,
      convertBetween,
      refreshRates,
      supportedCurrencies: SUPPORTED_CURRENCIES,
    }),
    [
      selectedCurrency,
      setSelectedCurrency,
      rates,
      isLoading,
      isStale,
      error,
      ratesLastUpdated,
      convert,
      convertBetween,
      refreshRates,
    ]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access currency context
 *
 * @throws Error if used outside of CurrencyProvider
 */
export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error(
      'useCurrency must be used within a CurrencyProvider. ' +
        'Make sure your component is wrapped with <CurrencyProvider>.'
    );
  }

  return context;
}

/**
 * Hook to optionally access currency context
 * Returns null if not within a CurrencyProvider (doesn't throw)
 */
export function useCurrencyOptional(): CurrencyContextValue | null {
  return useContext(CurrencyContext);
}

/**
 * Hook to get just the selected currency
 */
export function useSelectedCurrency(): Currency {
  const { selectedCurrency } = useCurrency();
  return selectedCurrency;
}

/**
 * Hook to get the convert function
 */
export function useConvertCurrency(): CurrencyContextValue['convert'] {
  const { convert } = useCurrency();
  return convert;
}
